import { z } from 'zod';
import { paginationQuerySchema, uuidSchema } from './common.js';
const text = (max) => z
    .string()
    .trim()
    .max(max)
    .refine((v) => !v.includes('\u0000'), 'Invalid character');
const atLeastOne = (v) => Object.keys(v).length > 0;
const flag = z.enum(['true', 'false']).transform((v) => v === 'true');
export const AUDIENCES = ['parents', 'staff', 'everyone', 'class', 'student'];
/** The audience decides which of classId and studentId must be given: exactly the one it needs, or neither. */
const audienceTarget = (v) => v.audience === undefined ||
    (v.audience === 'class' && !!v.classId && !v.studentId) ||
    (v.audience === 'student' && !!v.studentId && !v.classId) ||
    (['parents', 'staff', 'everyone'].includes(v.audience) && !v.classId && !v.studentId);
const targetIssue = {
    message: 'Choose a class for a class audience, a student for a student audience, and neither for the others',
    path: ['audience'],
};
/** A title becomes an email subject, so it must stay on one line: a line break there could add mail headers. */
const singleLine = text(200)
    .min(1)
    .refine((v) => !/[\r\n]/.test(v), 'The title must be a single line');
const announcementFields = {
    title: singleLine,
    body: text(5000).min(1),
    audience: z.enum(AUDIENCES),
    classId: uuidSchema.nullable().optional(),
    studentId: uuidSchema.nullable().optional(),
    channelEmail: z.boolean(),
    channelSms: z.boolean(),
};
export const createAnnouncementSchema = z
    .object({
    ...announcementFields,
    channelEmail: announcementFields.channelEmail.default(false),
    channelSms: announcementFields.channelSms.default(false),
})
    .refine(audienceTarget, targetIssue);
export const updateAnnouncementSchema = z
    .object(announcementFields)
    .partial()
    .refine(atLeastOne, 'At least one field is required')
    .refine(audienceTarget, targetIssue);
/** Publishing now, or later when `publishAt` (an instant with its offset) is in the future. */
export const publishAnnouncementSchema = z.object({
    publishAt: z.string().datetime({ offset: true }).optional(),
});
export const listAnnouncementsQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['draft', 'scheduled', 'published', 'cancelled']).optional(),
});
export const audiencePreviewQuerySchema = z
    .object({
    audience: z.enum(AUDIENCES),
    classId: uuidSchema.optional(),
    studentId: uuidSchema.optional(),
})
    .refine(audienceTarget, targetIssue);
export const listDeliveriesQuerySchema = paginationQuerySchema.extend({
    status: z.enum(['pending', 'sending', 'sent', 'failed', 'cancelled']).optional(),
    channel: z.enum(['email', 'sms']).optional(),
    announcementId: uuidSchema.optional(),
});
export const listNotificationsQuerySchema = paginationQuerySchema.extend({
    unreadOnly: flag.optional(),
});
export const setPreferenceSchema = z.object({
    channel: z.enum(['email', 'sms']),
    enabled: z.boolean(),
});
/** A phone number the gateway can take: optional +, then 8 to 15 digits once spaces and punctuation are removed. */
export function normalizePhone(raw) {
    const cleaned = raw.replace(/[\s\-().]/g, '');
    return /^\+?[0-9]{8,15}$/.test(cleaned) ? cleaned : null;
}
/** An address with one @, text on both sides, and a dot in the domain. */
export function isEmailAddress(raw) {
    return raw.length <= 320 && /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(raw.trim());
}
/** A text message is one short line: title and body joined and cut to what a gateway accepts. */
export function smsText(title, body, max = 480) {
    const joined = `${title}: ${body}`.replace(/\s+/g, ' ').trim();
    return joined.length <= max ? joined : `${joined.slice(0, max - 1)}…`;
}
//# sourceMappingURL=communication.js.map